import { useCallback, useEffect, useState } from 'react';
import type { Eu, Falha } from '../../acesso';
import type { Sessao } from '../../auth';
import { lerQualidadeDaEquipe, salvarPoliticaDeQualidade } from '../../auditoria';
import type {
  DiaDaQualidade,
  QualidadeDaEquipe as Dados,
  QualidadeDaPessoa,
} from '../../auditoria';
import { ErroDeArea, EstadoVazio, SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { papeisDe, ultimaCentral } from './comum';
import './divergencias.css';

/**
 * QUALIDADE DA EQUIPE (dono/Carlos, 14/10/2026) — dois indicadores que nunca se misturam:
 *
 *   FALHA DE REPOSIÇÃO confirmadas ÷ produtos mandados repor → ≥ 30% bloqueia o dia seguinte
 *     ("o que importa é se o produto tinha quando ele falou que não achou pra repor")
 *   ERRO DE CONTAGEM   confirmados ÷ produtos contados → só acompanhamento (a prateleira vende)
 *
 * Desligada, a tela mostra o "bloqueio previsto". Ligar/desligar é do administrador, com motivo.
 */

type Periodo = 'hoje' | 'ontem' | '7' | '30' | 'periodo';
const PERIODOS: readonly { v: Periodo; r: string }[] = [
  { v: 'hoje', r: 'Hoje' },
  { v: 'ontem', r: 'Ontem' },
  { v: '7', r: '7 dias' },
  { v: '30', r: '30 dias' },
  { v: 'periodo', r: 'Escolher datas' },
];
const hojeBR = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
const mais = (dia: string, n: number) => {
  const d = new Date(`${dia}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const ddmm = (dia: string) => `${dia.slice(8, 10)}/${dia.slice(5, 7)}`;
const pct = (n: number) => `${String(n).replace('.', ',')}%`;

function intervalo(p: Periodo, de: string, ate: string): { de: string; ate: string } {
  const h = hojeBR();
  if (p === 'hoje') return { de: h, ate: h };
  if (p === 'ontem') return { de: mais(h, -1), ate: mais(h, -1) };
  if (p === '7') return { de: mais(h, -6), ate: h };
  if (p === '30') return { de: mais(h, -29), ate: h };
  const d = /^\d{4}-\d{2}-\d{2}$/.test(de) ? de : h;
  return { de: d, ate: /^\d{4}-\d{2}-\d{2}$/.test(ate) && ate >= d ? ate : d };
}

function SituacaoDoBloqueio({ d, ligada }: { d: DiaDaQualidade; ligada: boolean }) {
  if (d.bloqueio)
    return d.bloqueio.liberado ? (
      <span className="dp-selo dp-selo-neutro" title={d.bloqueio.liberadoMotivo ?? undefined}>
        <Icone nome="check" tamanho={12} traco={2} />
        Liberado
      </span>
    ) : (
      <span className="dp-selo dp-selo-critico">
        <Icone nome="lock" tamanho={12} traco={2} />
        Bloqueado {ddmm(d.bloqueio.dia)}
      </span>
    );
  if (d.avaliacao.bloqueioPrevisto)
    return (
      <span className="dp-selo dp-selo-atencao">
        <Icone nome="warn" tamanho={12} traco={2} />
        {ligada ? 'Bloqueia' : 'Bloqueio previsto'}
      </span>
    );
  return <span className="dp-card-meta">—</span>;
}

function Pessoa({
  p,
  ligada,
  casosEm,
}: {
  p: QualidadeDaPessoa;
  ligada: boolean;
  casosEm: string;
}) {
  const [aberto, setAberto] = useState(false);
  const ultimo = p.dias[0];
  // Cada produto com caso aberto conta uma vez (o mesmo caso não soma nos dois indicadores).
  const pendentes = Math.max(p.total.pendentesContagem, p.total.pendentesReposicao);
  return (
    <li className="dp-qe-pessoa">
      <div className="dp-qe-cab">
        <strong className="dp-qe-nome">{p.nome}</strong>
        {ultimo && <SituacaoDoBloqueio d={ultimo} ligada={ligada} />}
      </div>
      <div className="dp-qe-indicadores">
        <div className="dp-qe-ind" data-alerta={p.diasComBloqueioPrevisto > 0}>
          <span className="dp-qe-rotulo">Falha de reposição · bloqueia</span>
          <strong className="dp-qe-taxa">{pct(p.taxaReposicao)}</strong>
          <span className="dp-card-meta">
            {p.total.falhasReposicao} de {p.total.tarefasReposicao} mandados repor
          </span>
        </div>
        <div className="dp-qe-ind">
          <span className="dp-qe-rotulo">Erro de contagem · acompanhamento</span>
          <strong className="dp-qe-taxa">{pct(p.taxaContagem)}</strong>
          <span className="dp-card-meta">
            {p.total.errosContagem} de {p.total.contados} produtos contados
          </span>
        </div>
        <div className="dp-qe-ind">
          <span className="dp-qe-rotulo">Pendentes de apuração</span>
          <strong className="dp-qe-taxa">{pendentes}</strong>
          <span className="dp-card-meta">
            {ultimo ? `Última apuração: Giro de ${ddmm(ultimo.dia)}` : 'Sem apuração'}
          </span>
        </div>
      </div>
      <div className="dp-acoes">
        <button
          type="button"
          className="dp-btn dp-btn-texto"
          aria-expanded={aberto}
          onClick={() => setAberto((a) => !a)}
        >
          <Icone nome={aberto ? 'chev-r' : 'chev-d'} tamanho={14} />
          {aberto ? 'Fechar dias' : `Ver dia a dia (${String(p.dias.length)})`}
        </button>
        <a className="dp-btn dp-btn-texto" href={casosEm}>
          <Icone nome="search" tamanho={14} />
          Ver casos e justificativas
        </a>
      </div>
      {aberto && (
        <ul className="dp-qe-dias">
          {p.dias.map((d) => (
            <li key={d.dia}>
              <span className="dp-qe-dia">Giro de {ddmm(d.dia)}</span>
              <span>
                Reposição <b>{pct(d.avaliacao.reposicao.taxa)}</b> ({d.conta.falhasReposicao}/
                {d.conta.tarefasReposicao})
                {d.conta.pendentesReposicao > 0 && (
                  <span className="dp-card-meta">
                    {' '}
                    · {d.conta.pendentesReposicao} pendente(s), pode chegar a{' '}
                    {pct(d.avaliacao.reposicao.taxaMaxima)}
                  </span>
                )}
              </span>
              <span className="dp-card-meta">
                Contagem {pct(d.avaliacao.contagem.taxa)} ({d.conta.errosContagem}/
                {d.conta.contados})
              </span>
              <SituacaoDoBloqueio d={d} ligada={ligada} />
              {d.bloqueio?.liberado && d.bloqueio.liberadoMotivo && (
                <span className="dp-card-meta">Liberação: {d.bloqueio.liberadoMotivo}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export function QualidadeDaEquipe({ sessao, eu }: { sessao: Sessao; eu: Eu | null }) {
  const papeis = papeisDe(eu);
  const [periodo, setPeriodo] = useState<Periodo>('7');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [dados, setDados] = useState<Dados | null>(null);
  const [falha, setFalha] = useState<Falha | null>(null);
  const [editando, setEditando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const per = intervalo(periodo, de, ate);

  const carregar = useCallback(async () => {
    const r = await lerQualidadeDaEquipe(per.de, per.ate);
    if (r.ok) {
      setDados(r.dados);
      setFalha(null);
    } else setFalha(r.falha);
  }, [per.de, per.ate]);
  useEffect(() => {
    if (papeis.gestao) void carregar();
  }, [carregar, papeis.gestao]);

  if (!papeis.gestao) return <SemAcesso area="A qualidade da equipe" />;
  if (falha && !dados)
    return (
      <section className="dp">
        <ErroDeArea oQue="a qualidade da equipe" aoTentar={() => void carregar()} />
      </section>
    );
  const pol = dados?.politica;
  const casosDe = (p: QualidadeDaPessoa) =>
    `#/divergencias?aba=historico&pessoa=${p.accountId}&per=periodo&de=${per.de}&ate=${per.ate}`;

  return (
    <section className="dp">
      <a className="dp-link" href={ultimaCentral()}>
        <Icone nome="arrow-l" tamanho={16} />
        Voltar às divergências
      </a>
      <header className="dp-topo">
        <div>
          <h1 className="dp-titulo">Qualidade da Equipe</h1>
          <p className="dp-sub">
            Falha de reposição (pode bloquear) e erro de contagem (só acompanhamento), por
            balconista.
          </p>
        </div>
        <div className="dp-chips" role="group" aria-label="Período (dia do Giro)">
          {PERIODOS.map((p) => (
            <button
              key={p.v}
              type="button"
              className="dp-chip"
              aria-pressed={periodo === p.v}
              onClick={() => setPeriodo(p.v)}
            >
              {p.r}
            </button>
          ))}
        </div>
      </header>
      {periodo === 'periodo' && (
        <div className="dp-filtros">
          <label className="dp-rotulo">
            De
            <input
              className="dp-campo"
              type="date"
              value={de}
              onChange={(e) => setDe(e.target.value)}
            />
          </label>
          <label className="dp-rotulo">
            Até
            <input
              className="dp-campo"
              type="date"
              value={ate}
              onChange={(e) => setAte(e.target.value)}
            />
          </label>
        </div>
      )}

      {aviso && (
        <div className="dp-salvo" role="status">
          <Icone nome="check" tamanho={16} traco={2} />
          {aviso}
        </div>
      )}

      {pol && (
        <section className="dp-area dp-qe-politica" aria-label="Regra do bloqueio">
          <div className="dp-area-cab">
            <h2 className="dp-area-titulo">Regra do bloqueio por falha de reposição</h2>
            <span className={`dp-selo ${pol.ativa ? 'dp-selo-ok' : 'dp-selo-atencao'}`}>
              {pol.ativa
                ? `Ligada${pol.desde ? ` desde ${ddmm(pol.desde)}` : ''}`
                : 'Desligada — em homologação'}
            </span>
          </div>
          <p className="dp-dica" style={{ margin: 0 }}>
            {pol.ativa
              ? `Quem tiver ${String(pol.limitePct)}% ou mais de falhas de reposição confirmadas (disse que não tinha e o estoque tinha) nos produtos mandados repor no Giro fica com a venda bloqueada no dia seguinte (horário de Brasília). Erro de contagem da prateleira não bloqueia.`
              : `Ainda não bloqueia ninguém por esta regra — o bloqueio em vigor continua o de antes. "Bloqueio previsto" mostra o que aconteceria com ${String(pol.limitePct)}%.`}
            {pol.minimoContados > 1
              ? ` Mínimo de ${String(pol.minimoContados)} produtos mandados repor no dia.`
              : ' Sem mínimo de produtos.'}
          </p>
          {papeis.administra && !editando && (
            <div>
              <button type="button" className="dp-btn" onClick={() => setEditando(true)}>
                <Icone nome="edit" tamanho={14} />
                {pol.ativa ? 'Alterar ou desligar' : 'Ligar a regra'}
              </button>
            </div>
          )}
          {papeis.administra && editando && (
            <FormPolitica
              ativa={pol.ativa}
              limite={pol.limitePct}
              minimo={pol.minimoContados}
              aoCancelar={() => setEditando(false)}
              aoSalvar={async (d) => {
                const r = await salvarPoliticaDeQualidade(sessao, d);
                if (r.tipo === 'confirmada') {
                  setEditando(false);
                  setAviso(
                    d.ativa ? 'Regra ligada: vale a partir do Giro de hoje.' : 'Regra salva.',
                  );
                  void carregar();
                  return null;
                }
                return r.tipo === 'incerta'
                  ? 'Não deu para confirmar (conexão). Tente de novo.'
                  : r.mensagem;
              }}
            />
          )}
        </section>
      )}

      {!dados ? (
        <p className="dp-dica" aria-busy="true">
          Carregando…
        </p>
      ) : dados.pessoas.length === 0 ? (
        <EstadoVazio
          icone="search"
          titulo="Nenhum Giro no período"
          texto="Escolha outro período para ver a apuração."
        />
      ) : (
        <ul className="dp-qe-lista">
          {dados.pessoas.map((p) => (
            <Pessoa key={p.accountId} p={p} ligada={pol?.ativa === true} casosEm={casosDe(p)} />
          ))}
        </ul>
      )}
    </section>
  );
}

function FormPolitica({
  ativa,
  limite,
  minimo,
  aoCancelar,
  aoSalvar,
}: {
  ativa: boolean;
  limite: number;
  minimo: number;
  aoCancelar: () => void;
  aoSalvar: (d: {
    ativa: boolean;
    limitePct: number;
    minimoContados: number;
    motivo: string;
  }) => Promise<string | null>;
}) {
  const [liga, setLiga] = useState(ativa);
  const [lim, setLim] = useState(String(limite));
  const [min, setMin] = useState(String(minimo));
  const [motivo, setMotivo] = useState('');
  const [confirmar, setConfirmar] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const limN = Number(lim);
  const minN = Number(min);
  const pronto =
    Number.isInteger(limN) &&
    limN >= 1 &&
    limN <= 100 &&
    Number.isInteger(minN) &&
    minN >= 1 &&
    motivo.trim().length >= 5;
  return (
    <form
      className="dp-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!pronto || enviando) return;
        if (!confirmar) {
          setConfirmar(true);
          return;
        }
        setEnviando(true);
        void aoSalvar({
          ativa: liga,
          limitePct: limN,
          minimoContados: minN,
          motivo: motivo.trim(),
        }).then((e2) => {
          setEnviando(false);
          setErro(e2);
        });
      }}
    >
      <label className="dp-check">
        <input
          type="checkbox"
          checked={liga}
          onChange={(e) => (setLiga(e.target.checked), setConfirmar(false))}
        />
        Regra ligada (bloqueia quem passar do limite)
      </label>
      <div className="dp-filtros">
        <label className="dp-rotulo">
          Limite (%)
          <input
            className="dp-campo"
            inputMode="numeric"
            value={lim}
            onChange={(e) => (setLim(e.target.value.replace(/\D/g, '')), setConfirmar(false))}
          />
        </label>
        <label className="dp-rotulo">
          Mínimo de produtos mandados repor
          <input
            className="dp-campo"
            inputMode="numeric"
            value={min}
            onChange={(e) => (setMin(e.target.value.replace(/\D/g, '')), setConfirmar(false))}
          />
        </label>
      </div>
      <label className="dp-rotulo">
        Motivo (obrigatório)
        <input
          className="dp-campo"
          value={motivo}
          maxLength={300}
          onChange={(e) => (setMotivo(e.target.value), setConfirmar(false))}
          placeholder="Ex.: homologado pela direção em 14/10"
        />
      </label>
      {confirmar && (
        <div className="dp-alerta dp-alerta-aviso" role="alert">
          <Icone nome="warn" tamanho={16} />
          {liga && !ativa
            ? `Confirma ligar? A partir do Giro de hoje, ${String(limN)}% ou mais de falhas de reposição bloqueia a venda no dia seguinte. Nada de antes é bloqueado.`
            : 'Confirma a mudança? Fica registrada com o seu nome e o motivo.'}
        </div>
      )}
      {erro && (
        <div className="dp-alerta dp-alerta-erro" role="alert">
          <Icone nome="warn" tamanho={16} />
          {erro}
        </div>
      )}
      <div className="dp-form-botoes">
        <button type="button" className="dp-btn dp-btn-texto" onClick={aoCancelar}>
          Cancelar
        </button>
        <button type="submit" className="dp-btn dp-btn-primario" disabled={!pronto || enviando}>
          {enviando ? 'Salvando…' : confirmar ? 'Confirmar' : 'Salvar'}
        </button>
      </div>
    </form>
  );
}
